import HBButton from "@/components/HBButton";
import HBIcon from "@/components/HBIcon";
import HBLoading from "@/components/HBLoading";
import HBMessage from "@/components/HBMessage";
import HBPopover from "@/components/HBPopover";
import { usePinActionContext } from "@/components/PurePinCard/actions/context";
import { UNCLASSIFIED_BOARD } from "@/constants/board";
import { LOCAL_STORAGE_KEY } from "@/constants/localStorageKey";
import useAppStore from "@/context/store";
import {
  checkPinExisted,
  createPin,
  getBoard,
  transferPinBoards,
} from "@/services";
import { filterBoards } from "@/utils/filterBoards";
import { getContentType } from "@/utils/getContentType";
import { isSafari } from "@/utils/isSafari";
import { safeParse } from "@/utils/safeParse";
import sortBoardsByTitleAndCreateTime, {
  setBoardsTitlePinyin,
} from "@/utils/sortBoardsByTitleAndCreateTime";
import useRequest from "@/utils/useReq";
import { useMemoizedFn } from "ahooks";
import { type PopoverProps } from "antd";
import classNames from "classnames";
import { groupBy, last } from "lodash";
import {
  ReactChild,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react";
import { Else, If, Then, When } from "react-if";
import { create } from "zustand";
import HBSearchInput from "../../HBSearchInput";
import BoardList from "./BoardList";
import styles from "./index.module.less";

const LAST_BOARD_NUM = 5;

interface IProps {
  contentRef?: RefObject<HTMLDivElement>;
  pin: IPin;
  onOk?: (pin: IPin) => void;
  searchWord?: string;
  boardMatchingResult?: any;
  onUserBoardUpdate?: (board: any) => void;
  currentBoard?: IBoardInfo; // 当前画板信息，用于画板选择器默认值
  enableCurrentBoardPriority?: boolean; // 是否启用当前画板优先逻辑，仅在 discovery/boards/[board] 页面使用
  enableBoardRecommend?: boolean; // 是否启用画板推荐功能
  createdPinForMove?: IPin; // 已采集的 pin 信息，用于移动画板
  onMoveComplete?: () => void; // 移动完成后的回调
}

export const useUnclassifiedBoard = create(() => ({
  record: {
    time: 0,
  },
}));

export function useLastBoards(
  currentBoard?: IBoardInfo,
  enableCurrentBoardPriority?: boolean,
  hideUnclassifiedBoard?: boolean // 是否隐藏未整理画板（仅在第一次自动弹窗时使用）
) {
  const record = useUnclassifiedBoard((state) => state.record);
  const boards = useAppStore((state) => state.lastBoards);

  useEffect(() => {
    const localRecord = safeParse(
      localStorage.getItem(LOCAL_STORAGE_KEY.LAST_UNCLASSIFIED_BOARD)
    );
    if (localRecord) {
      useUnclassifiedBoard.setState({
        record: localRecord,
      });
    }
  }, []);

  const setRecord = useMemoizedFn((record: { time: number }) => {
    useUnclassifiedBoard.setState({
      record,
    });
    try {
      window.localStorage.setItem(
        LOCAL_STORAGE_KEY.LAST_UNCLASSIFIED_BOARD,
        JSON.stringify(record)
      );
    } catch (e) {
      console.error("本地缓存未分类画板失败");
    }
  });

  const updateUnclassifiedBoardUpdateTime = useMemoizedFn(() => {
    setRecord({
      time: Date.now() / 1000,
    });
  });

  const lastBoards = useMemo(() => {
    const boardsArray = Array.isArray(boards) ? boards : [];
    let sortedBoards = [...boardsArray];

    // 如果隐藏未整理画板（第一次自动弹窗模式），过滤掉已存在的未整理画板
    if (hideUnclassifiedBoard) {
      sortedBoards = sortedBoards.filter(
        (board) => board.board_id !== 0 && board.title !== "未整理的采集"
      );
    } else {
      // 正常模式：添加未整理画板（如果不存在）
      const hasUnclassifiedBoard = sortedBoards.some(
        (board) => board.board_id === 0 || board.title === "未整理的采集"
      );
      if (!hasUnclassifiedBoard) {
        sortedBoards.push({
          ...UNCLASSIFIED_BOARD,
          updated_at: record?.time || new Date("1990-01-01").getTime() / 1000,
        });
      }
    }

    sortedBoards = sortedBoards.sort((a, b) => b.updated_at - a.updated_at);

    // 如果启用当前画板优先逻辑，将当前画板移到最前面
    if (
      enableCurrentBoardPriority &&
      currentBoard &&
      !currentBoard?.extra?.is_creation
    ) {
      const currentBoardIndex = sortedBoards.findIndex(
        (board) => board.board_id === currentBoard.board_id
      );

      if (currentBoardIndex > 0) {
        // 移除当前画板并将其插入到最前面
        const [currentBoardItem] = sortedBoards.splice(currentBoardIndex, 1);
        sortedBoards.unshift(currentBoardItem);
      }
    }

    return sortedBoards;
  }, [
    boards,
    currentBoard,
    enableCurrentBoardPriority,
    hideUnclassifiedBoard,
    record?.time,
  ]);

  return {
    lastBoards,
    recordTime: record?.time,
    setRecord,
    updateUnclassifiedBoardUpdateTime,
    hideBoardSelect: false,
  };
}

export function usePinyinSortBoards(
  userLastBoards: (IBoardInfo & {
    _pinyin?: Array<Array<string>>;
    _firstLetter?: string;
    _icon?: string;
  })[],
  currentBoard?: IBoardInfo,
  enableCurrentBoardPriority?: boolean,
  hideUnclassifiedBoard?: boolean // 是否隐藏未整理画板（仅在第一次自动弹窗时使用）
) {
  const record = useUnclassifiedBoard((state) => state.record);
  const { data: boardData, loading: boardLoading } = useRequest(
    async () => {
      const list = [...userLastBoards];

      // const result = await getLastBoards({ extra: "cover" });
      // 将画板增加拼音
      let boardList = list;
      if (
        boardList?.length > 0 &&
        !boardList?.every((board) => board?._pinyin)
      ) {
        boardList = await setBoardsTitlePinyin(boardList);
      }
      // 过滤原创画板
      let boards =
        boardList?.filter((board) => !board?.extra?.is_creation) || [];
      // 如果隐藏未整理画板（第一次自动弹窗模式或已采集至未整理），过滤掉它
      if (hideUnclassifiedBoard) {
        boards = boards.filter(
          (board) => board.board_id !== 0 && board.title !== "未整理的采集"
        );
      } else {
        // 正常模式：添加未整理画板（如果不存在）
        const hasUnclassifiedBoard = boards.some(
          (board) => board.board_id === 0 || board.title === "未整理的采集"
        );
        if (!hasUnclassifiedBoard) {
          boards.push({
            ...UNCLASSIFIED_BOARD,
            updated_at: record?.time || new Date("1990-01-01").getTime() / 1000,
          } as any);
        }
      }
      // 为画板添加首字母与拼音
      const withPinyinBoards = boards.map((board) => {
        const item = { ...board };
        let firstLetter = `${
          item._pinyin[0]?.length > 0
            ? item._pinyin[0][0]
            : item._pinyin[0] || ""
        }`.toLocaleUpperCase();
        if (!/[A-Z]/.test(firstLetter)) {
          firstLetter = "#";
        }
        item._firstLetter = firstLetter;

        return item;
      });

      // 分离未整理画板和其他画板
      const unclassifiedBoard = withPinyinBoards.find(
        (board) => board.board_id === 0 || board.title === "未整理的采集"
      );
      const otherUserBoards = withPinyinBoards.filter(
        (board) => board.board_id !== 0 && board.title !== "未整理的采集"
      );

      // 根据当前画板状态决定画板排序
      let sortedBoards = [...otherUserBoards];

      // 仅在 discovery/boards/[board] 页面启用当前画板优先逻辑
      // 如果当前画板不是原创画板，且在画板列表中，则将其移到最前面
      if (
        enableCurrentBoardPriority &&
        currentBoard &&
        !currentBoard?.extra?.is_creation
      ) {
        const currentBoardIndex = sortedBoards.findIndex(
          (board) => board.board_id === currentBoard.board_id
        );

        if (currentBoardIndex > 0) {
          // 移除当前画板并将其插入到最前面
          const [currentBoardItem] = sortedBoards.splice(currentBoardIndex, 1);
          sortedBoards.unshift(currentBoardItem);
        }
      }

      // 前5个画板作为历史画板
      // 如果用户没有任何画板（包括未整理），不显示"采集至画板"标题
      const lastBoards = sortedBoards
        .slice(0, LAST_BOARD_NUM)
        .map((board, index) => {
          const item = {
            ...board,
          };
          // 只有当有画板时才显示"采集至画板"标题
          if (index === 0 && sortedBoards.length > 0) {
            item._icon = "time";
          }
          return item;
        });

      // 剩余画板按字母排序
      const otherBoards = Object.entries(
        groupBy(
          sortBoardsByTitleAndCreateTime(
            sortedBoards.slice(LAST_BOARD_NUM, sortedBoards.length)
          ),
          "_firstLetter"
        )
      )
        .sort((a, b) => `${a[0]}`.charCodeAt(0) - `${b[0]}`.charCodeAt(0))
        .reduce((result, [firstLetter, group]) => {
          result = result.concat(
            group.map((board, index) => {
              const item = { ...board };
              if (index === 0) {
                item._icon = firstLetter;
              }
              return item;
            })
          );
          return result;
        }, []);

      // 如果存在未整理画板且未被隐藏，将其放在最前面，并设置特殊标识
      const unclassifiedBoardItem = unclassifiedBoard
        ? {
            ...unclassifiedBoard,
            _icon: "unclassified", // 使用特殊标识，在 BoardItem 中会显示"先采集后整理"
          }
        : null;

      // 组合画板列表：未整理画板（如果有） + "采集至画板"标题 + 其他画板
      const finalBoards = unclassifiedBoardItem
        ? [unclassifiedBoardItem, ...lastBoards, ...otherBoards]
        : [...lastBoards, ...otherBoards];

      return {
        groupByLetterBoards: finalBoards,
        withPinyinBoards,
      };
    },
    {
      cacheKey: "pinCreatorBoards",
      refreshDeps: [
        userLastBoards,
        currentBoard?.board_id,
        enableCurrentBoardPriority,
        hideUnclassifiedBoard,
      ],
    }
  );

  return {
    boardData,
    boardLoading,
  };
}

export function PinDropdownView({
  contentRef,
  pin,
  onOk,
  searchWord,
  boardMatchingResult,
  onUserBoardUpdate,
  currentBoard,
  enableCurrentBoardPriority,
  enableBoardRecommend = true,
  createdPinForMove,
  onMoveComplete,
}: IProps) {
  const pinId = pin?.pin_id;
  const [keyword, setKeyword] = useState("");

  const searchInputRef = useRef<{ input: HTMLInputElement }>(null);

  // 先获取 existPin，用于判断是否已采集至未整理
  const { data: existPin } = useRequest(
    async () => {
      if (!pinId) {
        return null;
      }

      const result: any = await checkPinExisted({
        pin_id: pinId,
      });
      return result?.exist_pin;
    },
    {
      refreshDeps: [pinId],
    }
  );

  // 如果是第一次采集且无画板（移动模式），隐藏未整理画板
  const hideUnclassifiedBoard = !!createdPinForMove;

  // 如果已采集至未整理，也需要隐藏未整理画板
  const isCollectedToUnclassified =
    existPin &&
    (existPin?.board?.board_id === 0 ||
      existPin?.board?.title === "未整理的采集");
  const shouldHideUnclassifiedBoard =
    hideUnclassifiedBoard || isCollectedToUnclassified;

  const { lastBoards: userLastBoards, updateUnclassifiedBoardUpdateTime } =
    useLastBoards(
      currentBoard,
      enableCurrentBoardPriority,
      shouldHideUnclassifiedBoard
    );

  useEffect(() => {
    // 在safari中，focus会导致页面滚动位置变化，所以不执行
    if (isSafari()) {
      return;
    }

    searchInputRef.current?.input?.focus({ preventScroll: true });
  }, []);

  useMemo(async () => {
    if (userLastBoards?.length <= 0) {
      const userLastBoards = await useAppStore.getState().refreshLastBoards();
      if (userLastBoards) {
        try {
          window.localStorage.setItem(
            LOCAL_STORAGE_KEY.UPDATE_LAST_BOARDS,
            JSON.stringify(userLastBoards)
          );
        } catch (e) {
          console.error("设置更新最近采集失败");
        }
      }
    }
  }, [userLastBoards?.length]);

  const onSelectBoard = useMemoizedFn(async (boardId) => {
    // 如果是移动模式（已有采集的 pin），使用移动 API
    if (createdPinForMove?.pin_id) {
      if (!boardId || boardId === 0) {
        // 如果选择的是"未整理"，不需要移动
        onMoveComplete && onMoveComplete();
        return;
      }

      try {
        const result = await transferPinBoards([
          {
            pin_id: createdPinForMove.pin_id,
            move_to: boardId,
          },
        ]);

        if (result && result[createdPinForMove.pin_id]?.moved) {
          // 移动成功，刷新画板列表
          await useAppStore.getState().refreshLastBoards();

          // 获取移动后的画板信息，用于更新提示
          try {
            const boardResult = await getBoard({ board_id: boardId, pins: 1 });
            if (boardResult && !boardResult.err && boardResult.board) {
              // 触发成功回调，更新 pin 信息
              if (onOk) {
                const updatedPin = {
                  ...createdPinForMove,
                  board_id: boardId,
                  board: boardResult.board,
                };
                onOk(updatedPin);
              }
            } else {
              // 触发成功回调，更新 pin 信息
              if (onOk) {
                const updatedPin = {
                  ...createdPinForMove,
                  board_id: boardId,
                };
                onOk(updatedPin);
              }
            }
          } catch (error) {
            console.error("获取画板信息失败:", error);
            // 触发成功回调，更新 pin 信息
            if (onOk) {
              const updatedPin = {
                ...createdPinForMove,
                board_id: boardId,
              };
              onOk(updatedPin);
            }
          }

          onMoveComplete && onMoveComplete();
        } else {
          HBMessage.error("移动失败，请重试");
        }
      } catch (error) {
        console.error("移动画板失败:", error);
        HBMessage.error("移动失败，请重试");
      }
      return;
    }

    // 正常采集流程
    let result;
    if (pin?.pin_id) {
      const data: any = {
        via: pin.pin_id,
        text: pin.raw_text,
      };

      if (boardId) {
        data.board_id = boardId;
      }

      result = await createPin(data);

      if (boardId === 0) {
        updateUnclassifiedBoardUpdateTime();
      }

      if (result && !result.err) {
        onOk && onOk(result.pin);
      }
    }
  });

  const onKeywordChange = useCallback((evt) => {
    const keyword = evt.target.value;
    setKeyword(keyword.slice(0, 32));
  }, []);

  const { boardData, boardLoading } = usePinyinSortBoards(
    userLastBoards,
    currentBoard,
    enableCurrentBoardPriority,
    shouldHideUnclassifiedBoard
  );

  const groupByLetterBoards = useMemo(() => {
    return boardData?.groupByLetterBoards || [];
  }, [boardData]);

  const searchBoards = useMemo(() => {
    let boards = filterBoards(keyword, boardData);

    return boards.map((board, index) => {
      // if (index === 0) {
      //   item._icon = "search";
      // }

      return { ...board };
    });
  }, [boardData, keyword]);

  const renderActions = useMemoizedFn((board) => {
    return (
      <HBButton
        type="primary"
        data-board-id={board?.board_id}
        data-gd-click="button_click"
        data-button-name="确认采集"
        data-search-word={searchWord}
        data-pin-detail
        size="small"
      >
        采集
      </HBButton>
    );
  });

  return (
    <div
      className={classNames([styles.board, existPin ? styles.existPin : null])}
      data-content-type={getContentType(pin)}
      ref={contentRef}
      data-search-word={searchWord}
    >
      <div className={styles.search}>
        <HBSearchInput
          value={keyword}
          wrapClassName={styles.searchInputWrap}
          onInput={onKeywordChange}
          onChange={onKeywordChange}
          placeholder="搜索画板"
          className={styles.searchInput}
          hideSearchBtn
          ref={searchInputRef}
        />
      </div>
      <When condition={existPin}>
        <div className={styles.warning}>
          <HBIcon className={styles.icon} type="ic_Warning" />
          {existPin?.board?.board_id === 0 ||
          existPin?.board?.title === "未整理的采集" ? (
            <div className={styles.text}>已采集至未整理</div>
          ) : (
            <>
              <div className={styles.text}>该图已在画板</div>
              <a
                className={styles.link}
                href={`/boards/${existPin?.board?.board_id}`}
                target="_blank"
              >
                {existPin?.board?.title}
              </a>
            </>
          )}
        </div>
      </When>
      <If condition={boardLoading && !boardData}>
        <Then>
          <div className={styles.loading}>
            <HBLoading size="24px" />
          </div>
        </Then>
        <Else>
          <BoardList
            dataSource={keyword ? searchBoards : groupByLetterBoards}
            keyword={keyword}
            onSelectBoard={onSelectBoard}
            boardMatchingResult={boardMatchingResult}
            onUserBoardUpdate={onUserBoardUpdate}
            searchWord={searchWord}
            renderActions={renderActions}
            enableBoardRecommend={enableBoardRecommend}
          />
        </Else>
      </If>
    </div>
  );
}
export function PinDropdown({
  children,
  visible,
  placement,
  popoverProps = {},
  ...props
}: IProps & {
  children: ReactChild;
  visible: boolean;
  popoverProps?: PopoverProps;
  placement: PopoverProps["placement"];
}) {
  const { setHoldPinAction } = usePinActionContext();

  useEffect(() => {
    if (visible) {
      setHoldPinAction?.(true);
    } else {
      setHoldPinAction?.(false);
    }
  }, [visible, setHoldPinAction]);

  return (
    <HBPopover
      content={<PinDropdownView {...props} />}
      overlayClassName={styles.dropdown}
      overlayStyle={{ minWidth: 340 }}
      arrowContent={null}
      placement={placement || "bottomLeft"}
      visible={visible}
      {...popoverProps}
      align={
        placement === "topLeft" ? { offset: [-12, 4] } : { offset: [-12, 0] }
      }
      getPopupContainer={() =>
        // @ts-ignore
        last(document.querySelectorAll(".ant-modal-wrap")) || document.body
      }
      autoAdjustOverflow
      destroyTooltipOnHide
    >
      {children}
    </HBPopover>
  );
}
